import type { Camera } from './camera';
import type { RenderState } from './render-state';

/**
 * (owner, Sep 25: "on desktop when driving fast … it still shows 90 or so, and the render doesn't go higher than about 1ms but see a
 * noticeable jerking") Drawing between the simulation's steps. The world steps at a fixed 60 a second and the screen draws at its own
 * rate, 90, 120 or 144; the picture showed the newest step, so on a 144 Hz screen it moved one frame in three by nothing and the others
 * by a whole step — at 40 m/s that is 21 px, then 0, then 21 (tools/audit/drive-pacing.mjs measured 881 frames of 2,500 with no step,
 * the picture's speed off by 77 % a frame on average). Now every moving thing is remembered as it was before each step, and a frame
 * draws it at the fraction of the next step the clock has reached (`GameLoop` passes it as `alpha`): the picture is a step behind the
 * simulation (16 ms) and moves the same distance every frame. The positions are put back after the frame, so nothing in the game
 * ever sees the drawn ones.
 */
export class FrameInterp {
  /** what moves: vehicles (x, y, angle, alt), people and animals (x, y, angle, z), projectiles (x, y) — as they were before the last step */
  private ents: (object & { x: number; y: number })[] = [];
  private prev = new Float64Array(0);
  private cur = new Float64Array(0);
  /** 0 a vehicle, 1 a person or an animal, 2 a projectile */
  private kinds = new Uint8Array(0);
  private n = 0;
  private drawn = 0;
  private cam = { x: 0, y: 0, zoom: 1, ok: false };
  private camNow = { x: 0, y: 0, zoom: 1 };
  /** off: drawn at the newest step, as before (?nointerp, for comparing) */
  on = true;

  /** Before each simulation step: remember where everything is. */
  capture(st: RenderState, cam: Camera): void {
    if (!this.on) return;
    const n = st.vehicles.length + st.people.length + st.projectiles.length;
    if (this.prev.length < n * 4) { this.prev = new Float64Array(n * 8); this.cur = new Float64Array(n * 8); this.kinds = new Uint8Array(n * 2); }
    const e = this.ents, p = this.prev, kd = this.kinds; let i = 0;
    for (const v of st.vehicles) { e[i] = v; kd[i] = 0; const k = i * 4; p[k] = v.x; p[k + 1] = v.y; p[k + 2] = v.angle; p[k + 3] = v.alt; i++; }
    for (const q of st.people) { e[i] = q; kd[i] = 1; const k = i * 4; p[k] = q.x; p[k + 1] = q.y; p[k + 2] = q.angle; p[k + 3] = q.z; i++; }
    for (const q of st.projectiles) { e[i] = q; kd[i] = 2; const k = i * 4; p[k] = q.x; p[k + 1] = q.y; p[k + 2] = 0; p[k + 3] = 0; i++; }
    this.n = i; e.length = i;
    this.cam.x = cam.x; this.cam.y = cam.y; this.cam.zoom = cam.zoom; this.cam.ok = true;
  }

  /** Before a frame is drawn: everything at `alpha` (0..1) of the way from before the last step to after it. Pair with `restore`. */
  apply(cam: Camera, alpha: number): void {
    this.drawn = 0;
    if (!this.on || !this.n) return;
    const a = alpha < 0 ? 0 : alpha > 1 ? 1 : alpha, e = this.ents, p = this.prev, c = this.cur, kd = this.kinds;
    for (let i = 0; i < this.n; i++) {
      const o = e[i] as { x: number; y: number; angle: number; alt: number; z: number }, k = i * 4, kind = kd[i];
      c[k] = o.x; c[k + 1] = o.y; c[k + 2] = kind < 2 ? o.angle : 0; c[k + 3] = kind === 0 ? o.alt : kind === 1 ? o.z : 0;
      const dx = o.x - p[k], dy = o.y - p[k + 1];
      if (dx * dx + dy * dy > 16) continue; // (over 4 m in a step, 240 m/s: a teleport, a respawn, a snap to the server — drawn where it is)
      o.x = p[k] + dx * a; o.y = p[k + 1] + dy * a;
      if (kind < 2) {
        let da = c[k + 2] - p[k + 2]; if (da > Math.PI) da -= Math.PI * 2; else if (da < -Math.PI) da += Math.PI * 2;
        o.angle = p[k + 2] + da * a;
        if (kind === 0) o.alt = p[k + 3] + (c[k + 3] - p[k + 3]) * a; else o.z = p[k + 3] + (c[k + 3] - p[k + 3]) * a;
      }
    }
    this.drawn = this.n;
    this.camNow.x = cam.x; this.camNow.y = cam.y; this.camNow.zoom = cam.zoom;
    if (this.cam.ok) {
      const dx = cam.x - this.cam.x, dy = cam.y - this.cam.y;
      if (dx * dx + dy * dy < 400) { cam.x = this.cam.x + dx * a; cam.y = this.cam.y + dy * a; cam.zoom = this.cam.zoom + (cam.zoom - this.cam.zoom) * a; }
    }
  }

  /** After the frame (world, HUD and phone drawn): the simulation's own positions back. */
  restore(cam: Camera): void {
    if (!this.drawn) return;
    const e = this.ents, c = this.cur, kd = this.kinds;
    for (let i = 0; i < this.drawn; i++) {
      const o = e[i] as { x: number; y: number; angle: number; alt: number; z: number }, k = i * 4, kind = kd[i];
      o.x = c[k]; o.y = c[k + 1];
      if (kind === 0) { o.angle = c[k + 2]; o.alt = c[k + 3]; } else if (kind === 1) { o.angle = c[k + 2]; o.z = c[k + 3]; }
    }
    this.drawn = 0;
    cam.x = this.camNow.x; cam.y = this.camNow.y; cam.zoom = this.camNow.zoom;
  }
}
